import { create } from 'zustand';

// Internal buffers for batching
let chainBuffer = {};
let futuresBuffer = {};
let batchTimer = null;

export const useOptionChainStore = create((set, get) => ({
    chainData: {},
    futuresData: {},
    status: 'disconnected',

    setStatus: (status) => set({ status }),

    // Helper to trigger the batch update
    _flushBatch: () => {
        set((state) => {
            const newChainData = { ...state.chainData };
            const newFuturesData = { ...state.futuresData };

            // Apply chain updates
            Object.entries(chainBuffer).forEach(([underlying, expiries]) => {
                if (!newChainData[underlying]) newChainData[underlying] = {};
                Object.entries(expiries).forEach(([expiry, strikes]) => {
                    if (!newChainData[underlying][expiry]) newChainData[underlying][expiry] = {};
                    Object.entries(strikes).forEach(([strike, exchanges]) => {
                        if (!newChainData[underlying][expiry][strike]) newChainData[underlying][expiry][strike] = {};
                        Object.entries(exchanges).forEach(([exchange, data]) => {
                            newChainData[underlying][expiry][strike][exchange] = {
                                ...newChainData[underlying][expiry][strike][exchange],
                                ...data
                            };
                        });
                    });
                });
            });

            // Apply futures updates
            Object.entries(futuresBuffer).forEach(([key, data]) => {
                newFuturesData[key] = {
                    ...newFuturesData[key],
                    ...data
                };
            });

            // Clear buffers after applying
            chainBuffer = {};
            futuresBuffer = {};
            batchTimer = null;

            return {
                chainData: newChainData,
                futuresData: newFuturesData
            };
        });
    },

    updateOptionChain: (data) => {
        if (Array.isArray(data)) {
            data.forEach(item => get()._bufferOptionUpdate(item));
        } else {
            get()._bufferOptionUpdate(data);
        }
    },

    _bufferOptionUpdate: (data) => {
        const { underlying, expiry, strike, exchange, type, ltp, ltt, ltp_change, is_mid, is_illiquid, bid, ask, mid, spread_pct } = data;
        if (!underlying || !expiry || !strike) return;
        const exchangeName = exchange || "MCX";

        if (!chainBuffer[underlying]) chainBuffer[underlying] = {};
        if (!chainBuffer[underlying][expiry]) chainBuffer[underlying][expiry] = {};
        if (!chainBuffer[underlying][expiry][strike]) chainBuffer[underlying][expiry][strike] = {};
        if (!chainBuffer[underlying][expiry][strike][exchangeName]) chainBuffer[underlying][expiry][strike][exchangeName] = {};

        const side = type.toLowerCase(); // 'ce' or 'pe'
        chainBuffer[underlying][expiry][strike][exchangeName][side] = { ltp, ltt, ltp_change, is_mid, is_illiquid, bid, ask, mid, spread_pct };

        if (!batchTimer) {
            batchTimer = setTimeout(() => get()._flushBatch(), 50); // 50ms batching for smooth UI
        }
    },

    updateFutures: (data) => {
        if (Array.isArray(data)) {
            data.forEach(item => get()._bufferFuturesUpdate(item));
        } else {
            get()._bufferFuturesUpdate(data);
        }
    },

    _bufferFuturesUpdate: (data) => {
        const { exchange, ltp, symbol, raw_symbol, expiry, underlying, bid, ask, mid, spread_pct, is_illiquid } = data;
        const key = `${exchange}_${underlying || 'GOLD'}_${expiry}`;

        futuresBuffer[key] = { exchange, ltp, symbol, raw_symbol, expiry, underlying: underlying || 'GOLD', bid, ask, mid, spread_pct, is_illiquid };

        if (!batchTimer) {
            batchTimer = setTimeout(() => get()._flushBatch(), 50); // 50ms batching for smooth UI
        }
    },

    deleteOption: (data) => {
        const { underlying, expiry, strike } = data;
        set((state) => {
            const newData = { ...state.chainData };
            if (newData[underlying] && newData[underlying][expiry]) {
                delete newData[underlying][expiry][strike];
            }
            return { chainData: newData };
        });
    },

    reset: () => {
        chainBuffer = {};
        futuresBuffer = {};
        if (batchTimer) clearTimeout(batchTimer);
        batchTimer = null;
        set({ chainData: {}, futuresData: {} });
    }
}));
